import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Input } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faDownload,
  faPlay,
  faClock,
  faUser,
  faVideo,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { usePexelsVideosCore } from "../../../cores/pexels/usePexelsVideos";
import { PexelsVideo } from "../../../cores/pexels/services/api";
import { useSocialPostCore } from "../../../cores/socialPost";
import InternalModal from "../../layout/InternalModal";
import { getBase64 } from "../../../cores/utils";
import "./VideoBankModal.scss";

interface Props {
  show: boolean;
  onHide: () => void;
}

export const VideoBankModal = ({ show, onHide }: Props) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVideo, setSelectedVideo] = useState<PexelsVideo | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  
  const {
    videos,
    loading,
    error,
    hasMore,
    searchVideos,
    loadPopularVideos,
    downloadVideo,
    clearVideos
  } = usePexelsVideosCore();
  
  const { addVideo } = useSocialPostCore();

  useEffect(() => {
    if (show) {
      loadPopularVideos();
    } else {
      clearVideos();
      setSearchQuery('');
      setSelectedVideo(null);
    }
  }, [show, loadPopularVideos, clearVideos]);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      searchVideos(searchQuery.trim());
    } else {
      loadPopularVideos();
    }
  }, [searchQuery, searchVideos, loadPopularVideos]);

  const handleVideoSelect = useCallback(
    async (video: PexelsVideo) => {
      try {
        setIsDownloading(true);
        setSelectedVideo(video);

        console.log("Seleccionando video:", video);

        // Descargar el video como Blob
        const videoBlob = await downloadVideo(video);
        console.log("Video descargado:", videoBlob);

        // Convertir el Blob a data URL (video/mp4) para poder enviarlo correctamente al backend
        const dataUrl = (await getBase64(videoBlob)) as string;

        // Crear el objeto PostVideo
        const postVideo = {
          id: `pexels-${video.id}`,
          name: `Pexels Video ${video.id}`,
          lastModified: Date.now(),
          size: videoBlob.size,
          type: "video/mp4",
          data: dataUrl,
          width: video.width,
          height: video.height,
          duration: video.duration,
        };

        console.log("Agregando video al post:", postVideo);

        // Agregar el video al estado
        addVideo(postVideo);

        // Cerrar el modal después de un pequeño delay para que se vea la selección
        setTimeout(() => {
          onHide();
        }, 500);
      } catch (err) {
        console.error("Error al descargar el video:", err);
        setIsDownloading(false);
      }
    },
    [downloadVideo, addVideo, onHide]
  );

  const handleLoadMore = useCallback(() => {
    if (hasMore && !loading) {
      if (searchQuery.trim()) {
        searchVideos(searchQuery.trim(), false);
      } else {
        loadPopularVideos(false);
      }
    }
  }, [hasMore, loading, searchQuery, searchVideos, loadPopularVideos]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <InternalModal
      active={show}
      onClose={onHide}
    >
      <div className="modern-video-bank">
        {/* Header */}
        <div className="video-bank-header">
          <div className="header-content">
            <div className="header-icon">
              <FontAwesomeIcon icon={faVideo} />
            </div>
            <div className="header-text">
              <h4>Banco de Videos</h4>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-section">
          <Form onSubmit={handleSearch}>
            <div className="search-container">
              <div className="search-input-wrapper">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <Input
                  type="text"
                  placeholder="Buscar videos..."
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
              <Button type="submit" className="search-button">
                <FontAwesomeIcon icon={faSearch} />
                Buscar
              </Button>
            </div>
          </Form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            {error}
          </div>
        )}

        {/* Videos Grid */}
        <div className="videos-section">
          <div className="videos-grid">
            {videos.map((video) => (
              <div 
                key={video.id}
                className={`video-card ${selectedVideo?.id === video.id ? 'selected' : ''}`}
                onClick={() => handleVideoSelect(video)}
              >
                <div className="video-thumbnail">
                  <img 
                    src={video.image} 
                    alt={`Video ${video.id}`}
                    className="thumbnail-image"
                  />
                  <div className="video-overlay">
                    <div className="play-button">
                      <FontAwesomeIcon icon={faPlay} />
                    </div>
                    <div className="video-duration">
                      <FontAwesomeIcon icon={faClock} className="me-1" />
                      {formatDuration(video.duration)}
                    </div>
                  </div>
                  {isDownloading && selectedVideo?.id === video.id && (
                    <div className="download-overlay">
                      <div className="download-spinner">
                        <FontAwesomeIcon icon={faDownload} spin />
                      </div>
                      <span>Descargando...</span>
                    </div>
                  )}
                </div>
                <div className="video-info">
                  <div className="video-title">
                    Video {video.id}
                  </div>
                  <div className="video-meta">
                    <div className="video-author">
                      <FontAwesomeIcon icon={faUser} className="me-1" />
                      {video.user.name}
                    </div>
                    <div className="video-resolution">
                      {video.width}×{video.height}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-section">
            <div className="loading-spinner">
              <FontAwesomeIcon icon={faDownload} spin />
            </div>
            <p>Cargando videos...</p>
          </div>
        )}

        {/* Load More Button */}
        {hasMore && !loading && (
          <div className="load-more-section">
            <Button onClick={handleLoadMore} className="load-more-button">
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              Cargar más videos
            </Button>
          </div>
        )}

        {/* Empty State */}
        {videos.length === 0 && !loading && !error && (
          <div className="empty-state">
            <div className="empty-icon">
              <FontAwesomeIcon icon={faVideo} />
            </div>
            <h5>No se encontraron videos</h5>
            <p className="text-muted">Intenta con otros términos de búsqueda</p>
          </div>
        )}
      </div>
    </InternalModal>
  );
};